import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { AlertCircle, FileText, Mail, Rocket, Users } from "lucide-react";
import { fetchAdminProjects, ProjectSummary, WorkflowApiError } from "../services/workflow";
import { formatDateTime, getProjectStatusMeta } from "../services/workflow-ui";

export function AdminDashboard() {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadProjects = async () => {
      try {
        const nextProjects = await fetchAdminProjects();
        if (isMounted) {
          setProjects(nextProjects);
        }
      } catch (loadError) {
        if (isMounted) {
          setError(
            loadError instanceof WorkflowApiError
              ? loadError.message
              : "Projekte konnten nicht geladen werden",
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadProjects();

    return () => {
      isMounted = false;
    };
  }, []);

  const stats = useMemo(() => {
    return {
      total: projects.length,
      pendingDrafts: projects.filter((project) => project.status === "draft_ready").length,
      pendingRevisions: projects.filter((project) => project.status === "revision_requested").length,
      pendingAssets: projects.filter((project) => project.status === "assets_pending").length,
      paymentIssues: projects.filter((project) => project.paymentIssue).length,
      live: projects.filter((project) => project.status === "live").length,
    };
  }, [projects]);

  const priorityProjects = useMemo(
    () =>
      projects
        .filter((project) =>
          project.paymentIssue ||
          ["questionnaire_received", "draft_ready", "revision_requested", "assets_pending"].includes(
            project.status,
          ),
        )
        .slice(0, 6),
    [projects],
  );

  const recentProjects = useMemo(() => projects.slice(0, 6), [projects]);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2">Workflow-Uebersicht</h1>
          <p className="text-gray-600">
            Anfrage, Entwurf, Freigabe, Revisionen und Finalisierung in einem Prozess.
          </p>
        </div>
        <Link
          to="/admin/projects"
          className="inline-flex items-center gap-2 rounded-lg bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
        >
          <FileText size={18} />
          Projekte oeffnen
        </Link>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="mb-3 flex items-center gap-2 text-gray-700">
            <Users size={18} />
            <span className="text-sm">Projekte gesamt</span>
          </div>
          <p className="text-3xl font-bold">{isLoading ? "…" : stats.total}</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="mb-3 flex items-center gap-2 text-amber-800">
            <Mail size={18} />
            <span className="text-sm">Versand offen</span>
          </div>
          <p className="text-3xl font-bold text-amber-900">{isLoading ? "…" : stats.pendingDrafts}</p>
        </div>
        <div className="rounded-xl border border-orange-200 bg-orange-50 p-4">
          <div className="mb-3 flex items-center gap-2 text-orange-800">
            <AlertCircle size={18} />
            <span className="text-sm">Revisionen offen</span>
          </div>
          <p className="text-3xl font-bold text-orange-900">
            {isLoading ? "…" : stats.pendingRevisions}
          </p>
        </div>
        <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-4">
          <div className="mb-3 flex items-center gap-2 text-cyan-800">
            <FileText size={18} />
            <span className="text-sm">Wartet auf Daten</span>
          </div>
          <p className="text-3xl font-bold text-cyan-900">{isLoading ? "…" : stats.pendingAssets}</p>
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="mb-3 flex items-center gap-2 text-red-800">
            <AlertCircle size={18} />
            <span className="text-sm">Zahlungsprobleme</span>
          </div>
          <p className="text-3xl font-bold text-red-900">{isLoading ? "…" : stats.paymentIssues}</p>
        </div>
        <div className="rounded-xl border border-green-200 bg-green-50 p-4">
          <div className="mb-3 flex items-center gap-2 text-green-800">
            <Rocket size={18} />
            <span className="text-sm">Bereits live</span>
          </div>
          <p className="text-3xl font-bold text-green-900">{isLoading ? "…" : stats.live}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <section className="rounded-2xl border border-gray-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Priorisierte To-Dos</h2>
              <p className="text-sm text-gray-600">Alles, was im Prozess gerade Aktion braucht.</p>
            </div>
            <Link to="/admin/projects" className="text-sm font-medium text-black hover:text-gray-700">
              Alle Projekte
            </Link>
          </div>

          <div className="space-y-3">
            {priorityProjects.length === 0 && !isLoading ? (
              <p className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-sm text-gray-500">
                Aktuell gibt es keine priorisierten Vorgaenge.
              </p>
            ) : null}

            {priorityProjects.map((project) => {
              const statusMeta = getProjectStatusMeta(project.status);
              const cardClassName = project.paymentIssue
                ? "block rounded-xl border border-red-200 bg-red-50 px-4 py-4 transition hover:border-red-300"
                : "block rounded-xl border border-gray-200 px-4 py-4 transition hover:border-gray-300 hover:bg-gray-50";

              return (
                <Link
                  key={project.id}
                  to={`/admin/projects/${project.id}`}
                  className={cardClassName}
                >
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-gray-900">{project.title}</p>
                      <p className="text-sm text-gray-600">
                        {project.customer.company || project.customer.name}
                      </p>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${statusMeta.className}`}>
                      {statusMeta.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {project.paymentIssue ? project.paymentIssue.message : statusMeta.description}
                  </p>
                </Link>
              );
            })}
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-6">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Zuletzt aktualisiert</h2>
            <p className="text-sm text-gray-600">Schneller Zugriff auf die letzten Bewegungen.</p>
          </div>

          <div className="space-y-3">
            {recentProjects.length === 0 && !isLoading ? (
              <p className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-sm text-gray-500">
                Noch keine Projekte vorhanden.
              </p>
            ) : null}

            {recentProjects.map((project) => {
              const statusMeta = getProjectStatusMeta(project.status);
              const cardClassName = project.paymentIssue
                ? "flex items-start justify-between gap-4 rounded-xl border border-red-200 bg-red-50 px-4 py-4 transition hover:border-red-300"
                : "flex items-start justify-between gap-4 rounded-xl border border-gray-200 px-4 py-4 transition hover:border-gray-300 hover:bg-gray-50";

              return (
                <Link
                  key={project.id}
                  to={`/admin/projects/${project.id}`}
                  className={cardClassName}
                >
                  <div>
                    <p className="font-medium text-gray-900">{project.title}</p>
                    <p className="text-sm text-gray-600">{project.customer.email}</p>
                    {project.paymentIssue ? (
                      <p className="mt-2 text-sm text-red-800">{project.paymentIssue.message}</p>
                    ) : null}
                    <p className="mt-2 text-xs text-gray-500">
                      Letzte Aenderung: {formatDateTime(project.updatedAt)}
                    </p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-medium ${statusMeta.className}`}>
                    {statusMeta.label}
                  </span>
                </Link>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
